// encounterData.js
// Shared data layer for the encounter form — used by both the Print Notes screen
// and the Sign-in sheet so the medication/allergy logic never diverges.
//   - formatters (dates, name, sex)
//   - FHIR/DB response -> form-data mappers (med name/sig/dose, fill date)
//   - loadEncounterClinical(patientid)  -> {allergies, medications, partial}
//   - fetchPatientDemographics(patientid) -> {name, dob, sex}
//   - printEncounterForPatient({...})    -> one-shot: load + build + print

import api from "./printApi";
import { buildSheet, buildDocument } from "./encounterForm";
import { printHtmlDocument } from "./printUtils";
import { getPracticeId } from "../config/practice";

const practiceid = getPracticeId();

/* ----------------------------- formatting ----------------------------- */

export const todayStr = () => {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};

// Tolerant date -> "MM / DD / YYYY" (handles ISO, mm/dd/yyyy, mm-dd-yyyy).
export const fmtDateSlash = (val) => {
  if (!val) return "";
  const s = String(val).trim();
  let m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[2]} / ${m[3]} / ${m[1]}`;
  m = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})/);
  if (m) return `${m[1].padStart(2, "0")} / ${m[2].padStart(2, "0")} / ${m[3]}`;
  return s;
};

export const fmtSex = (g) => {
  if (!g) return "";
  const c = String(g).trim().charAt(0).toUpperCase();
  return c === "M" || c === "F" ? c : String(g).trim();
};

export const fmtName = (last, first) =>
  [last, first].filter(Boolean).join(", ").toUpperCase() || "—";

export const fmtTime = (t) => {
  if (!t) return "";
  const m = String(t).match(/^(\d{1,2}):(\d{2})/);
  if (!m) return String(t);
  let h = parseInt(m[1], 10);
  const ap = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${h}:${m[2]} ${ap}`;
};

// Age in whole years from a DOB (ISO or mm/dd/yyyy). "" if unparseable.
export const ageFromDob = (val) => {
  if (!val) return "";
  const s = String(val).trim();
  let y, mo, da;
  let m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) {
    y = +m[1]; mo = +m[2]; da = +m[3];
  } else {
    m = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})/);
    if (!m) return "";
    mo = +m[1]; da = +m[2]; y = +m[3];
  }
  const now = new Date();
  let age = now.getFullYear() - y;
  const md = now.getMonth() + 1 - mo;
  if (md < 0 || (md === 0 && now.getDate() < da)) age -= 1;
  return age >= 0 && age < 150 ? String(age) : "";
};

// Timestamp ("2026-06-15 05:12:58" or ISO / mm/dd/yyyy) -> compact MM/DD/YY.
const fmtTsShort = (val) => {
  if (!val) return "";
  let m = String(val).match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) return `${m[2].padStart(2, "0")}/${m[3].padStart(2, "0")}/${m[1].slice(-2)}`;
  m = String(val).match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
  if (m) return `${m[1].padStart(2, "0")}/${m[2].padStart(2, "0")}/${m[3].slice(-2)}`;
  return "";
};

/* ----------------------- FHIR/DB response -> form data ---------------------- */

// Split a FHIR medication display ("Protonix, delayed release tablet 40 mg")
// into a clean name ("Protonix") + a Dose token ("40 mg").
const STRENGTH_RE = /(\d+(?:\.\d+)?\s?(?:mg|mcg|g)\/\d+(?:\.\d+)?\s?ml|\d+(?:\.\d+)?\s?%|\d+(?:\.\d+)?\s?(?:mcg|mg|grams?|g|ml|units?|unit|iu|meq)\b)/i;
const DOSE_FORM_RE = /\b(?:delayed[- ]release|extended[- ]release|immediate[- ]release|sustained[- ]release|controlled[- ]release|enteric[- ]coated|tablets?|tabs?|capsules?|caps?|caplets?|softgels?|gelcaps?|oral|solution|suspension|syrup|elixir|aerosol|inhaler|inhalation|hfa|mdi|nebulizer|injection|injectable|cream|ointment|lotion|gel|patch(?:es)?|suppositor(?:y|ies)|drops?|sprays?|powder|packet|lozenges?|troches?|mouthwash|rinse|concentrate|er|xr|sr|cr|dr|odt)\b/gi;
export const splitNameDose = (rawName) => {
  let name = String(rawName || "").trim();
  let dose = "";
  const m = name.match(STRENGTH_RE);
  if (m) {
    dose = m[1].replace(/\s+/g, " ").trim();
    name = name.replace(m[0], " ");
  }
  name = name
    .replace(DOSE_FORM_RE, " ")
    .replace(/[,/]+/g, " ")
    .replace(/\s{2,}/g, " ")
    .replace(/^[\s,-]+|[\s,-]+$/g, "")
    .trim();
  if (!name) name = String(rawName || "").trim();
  return { name, dose };
};

// Hard length caps so a stray long value can never overflow its column.
export const truncate = (s, n) => {
  const str = String(s || "").trim();
  return str.length > n ? `${str.slice(0, n - 1).trimEnd()}…` : str;
};

// Collapse a verbose FHIR sig into standard clinical shorthand. Ordered rules —
// multi-word phrases must run before the single-word fallbacks they contain.
const SIG_RULES = [
  [/\(\s*\d+\.?\d*\s*(?:mg|mcg|g|ml|units?|meq)\s*total\s*\)/gi, " "],
  [/^\s*(?:take|takes|taking|inject|apply|use|instill|inhale|chew|dissolve|place|insert|spray|swallow|give)\s+/i, ""],
  [/\bby (?:oral|mouth) route\b/gi, "PO"],
  [/\bby mouth\b/gi, "PO"],
  [/\boral route\b/gi, "PO"],
  [/\borally\b/gi, "PO"],
  [/\boral\b/gi, "PO"],
  [/\bby subcutaneous route\b/gi, "SubQ"],
  [/\bsubcutaneous(?:ly)?\b/gi, "SubQ"],
  [/\bsub-?q\b/gi, "SubQ"],
  [/\bintramuscular(?:ly)?\b/gi, "IM"],
  [/\bintravenous(?:ly)?\b/gi, "IV"],
  [/\bsublingual(?:ly)?\b/gi, "SL"],
  [/\btransdermal(?:ly)?\b/gi, "TD"],
  [/\b(?:per rectum|rectal(?:ly)?)\b/gi, "PR"],
  [/\btopical(?:ly)?\b/gi, "top"],
  [/\b(?:by inhalation|inhalation|inhaled)\b/gi, "INH"],
  [/\b(?:intranasal(?:ly)?|nasal(?:ly)?)\b/gi, "NAS"],
  [/\bophthalmic(?:ally)?\b/gi, "OPHTH"],
  [/\botic(?:ally)?\b/gi, "OTIC"],
  [/\bevery other day\b/gi, "QOD"],
  [/\bevery (\d+)\s*(?:to|-)\s*\d+\s*hours?\b/gi, "q$1h"],
  [/\bevery (\d+) hours?\b/gi, "q$1h"],
  [/\b(?:four times|4 times) (?:a day|per day|daily)\b/gi, "QID"],
  [/\b(?:three times|thrice|3 times) (?:a day|per day|daily)\b/gi, "TID"],
  [/\b(?:twice|two times|2 times) (?:a day|per day|daily)\b/gi, "BID"],
  [/\b(?:once|one time|1 time) (?:a day|per day|daily)\b/gi, "daily"],
  [/\b(?:once daily|every day|each day)\b/gi, "daily"],
  [/\b(\d+) times (?:a day|per day|daily)\b/gi, "$1x/day"],
  [/\bevery morning\b/gi, "QAM"],
  [/\bevery evening\b/gi, "QPM"],
  [/\b(?:at bedtime|at night|nightly|before bed|every night)\b/gi, "QHS"],
  [/\b(?:once (?:a|per) week|every week|weekly)\b/gi, "weekly"],
  [/\b(?:a|per) day\b/gi, "daily"],
  [/\bas needed\b/gi, "PRN"],
  [/\b(?:if|when) needed\b/gi, "PRN"],
  [/\btablets?\b/gi, "tab"],
  [/\bcapsules?\b/gi, "cap"],
  [/\bdrops?\b/gi, "gtt"],
  [/\bfor (\d+) days?\b/gi, "x$1d"],
  [/\bfor (\d+) weeks?\b/gi, "x$1wk"],
  [/\bfor (\d+) months?\b/gi, "x$1mo"],
];

export const abbreviateSig = (raw) => {
  let s = String(raw || "").trim();
  if (!s) return "";
  SIG_RULES.forEach(([re, rep]) => {
    s = s.replace(re, rep);
  });
  return s
    .replace(/\s{2,}/g, " ")
    .replace(/\s+([.,;])/g, "$1")
    .replace(/[.,;\s]+$/, "")
    .trim();
};

// fhir-mirror medications endpoint -> [{name, dose, sig, filled}] (active, deduped).
export const mapMedications = (res) => {
  const groups = res && Array.isArray(res.data) ? res.data : [];
  const out = [];
  const seen = new Set();
  groups.forEach((group) => {
    const list = Array.isArray(group) ? group : [group];
    const rec = list.find((r) => r && r.status === "OPEN" && !r.isdiscontinued);
    if (!rec) return;
    const rawName = rec.medication || rec.medicationtallmanname;
    if (!rawName) return;
    const key = String(rawName).toLowerCase().trim();
    if (seen.has(key)) return;
    seen.add(key);
    const { name, dose } = splitNameDose(rawName);
    out.push({
      name: truncate(name, 34),
      dose: truncate(dose, 14),
      sig: truncate(abbreviateSig(rec.unstructuredsig), 64),
      filled: "", // FHIR MedicationRequest has no dispense/fill date
      prescribed: "",
    });
  });
  return out;
};

// Parse Athena MM/DD/YYYY (or MM/DD/YY) -> sortable YYYYMMDD (0 when absent).
const fillSortKey = (s) => {
  const m = String(s || "").match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
  if (!m) return 0;
  const yy = m[3].length === 2 ? `20${m[3]}` : m[3];
  return Number(yy) * 10000 + Number(m[1]) * 100 + Number(m[2]);
};

// Compact MM/DD/YY for the narrow Date Filled column.
const fmtFilled = (s) => {
  const m = String(s || "").match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
  if (!m) return "";
  return `${m[1].padStart(2, "0")}/${m[2].padStart(2, "0")}/${m[3].slice(-2)}`;
};

// current_medications DB rows (reconciled ACTIVE list from careiq) ->
// [{name, dose, sig, filled}], sorted by date filled (most recent first).
export const mapCurrentMeds = (res) => {
  const arr = res && Array.isArray(res.data) ? res.data : Array.isArray(res) ? res : [];
  return arr
    .filter(
      (r) =>
        r &&
        String(r.medication || "").trim() &&
        String(r.status || "active").toLowerCase() === "active"
    )
    .map((r) => {
      const split = splitNameDose(r.medication);
      const dose = String(r.dose || "").trim() || split.dose;
      return {
        name: truncate(split.name, 34),
        dose: truncate(dose, 14),
        sig: truncate(abbreviateSig(r.sig), 64),
        filled: fmtFilled(r.last_filled_date),
        prescribed: fmtFilled(r.last_prescribed_date),
        _fk: fillSortKey(r.last_filled_date),
      };
    })
    .sort((a, b) => b._fk - a._fk)
    .map(({ _fk, ...m }) => m);
};

// fhir-mirror allergies endpoint (raw array) -> [{allergen, reaction}].
export const mapAllergies = (res) => {
  const arr = Array.isArray(res) ? res : res && Array.isArray(res.data) ? res.data : [];
  return arr
    .filter((a) => a && String(a.title || "").trim())
    .map((a) => ({
      allergen: a.title,
      reaction: a.reaction && a.reaction !== "unassigned" ? a.reaction : "",
    }));
};

// surgical-history-proprietary endpoint ({data:{procedures:[...]}}) ->
// [{name, date}], sorted newest first.
export const mapSurgeries = (res) => {
  const data = res && res.data !== undefined ? res.data : res;
  const arr = data && Array.isArray(data.procedures)
    ? data.procedures
    : Array.isArray(data) ? data : [];
  return arr
    .filter((p) => p && String(p.description || "").trim())
    .map((p) => ({
      name: truncate(p.description, 42),
      date: fmtFilled(p.proceduredate),
      _fk: fillSortKey(p.proceduredate),
    }))
    .sort((a, b) => b._fk - a._fk)
    .map(({ _fk, ...s }) => s);
};

// rpm/data endpoint ({data:[rpm_master row]}) -> {enrolled, lastReading}.
// A row in rpm_master means the patient is enrolled; status 'I'/'0' = inactive.
export const mapRpm = (res) => {
  const arr = res && Array.isArray(res.data) ? res.data : [];
  if (!arr.length) return { enrolled: false, lastReading: "" };
  const active = arr.find((r) => {
    const st = String(r && r.status != null ? r.status : "").trim().toUpperCase();
    return st !== "I" && st !== "0" && st !== "INACTIVE";
  });
  const rec = active || arr[0];
  return {
    enrolled: Boolean(active),
    // lastReading is filled from bpp-master/latest (the real last BP reading);
    // rpm_master.lastrefresh is only an enrollment/sync stamp, not a reading.
    lastReading: "",
  };
};

// rpm/bpp-master/latest endpoint ({data:[{patientid, datetime, ...}]}) — latest
// reading per patient for the practice. Pull this patient's reading date.
export const mapLatestReading = (res, patientid) => {
  const arr = res && Array.isArray(res.data) ? res.data : [];
  const rec = arr.find((r) => String(r.patientid) === String(patientid));
  return rec && rec.datetime ? fmtTsShort(rec.datetime) : "";
};

const fmtPhone = (p) => {
  const d = String(p || "").replace(/\D/g, "");
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : String(p || "");
};

// insurance-summary endpoint (api/fhir/coverage.py) -> [{type, plan, policy}] for
// the primary + secondary coverages. Ranking / active-filtering now lives
// server-side in summarize_coverage_bundle(), so this is a thin UI adapter: it
// just labels the rank, drops empties, and caps strings so a long payer name
// can't overflow the narrow column. Accepts the summary object
// ({primary, secondary, coverages}) and also tolerates the older bare array so
// it survives either deploy order.
const INS_RANK = (t, i) => {
  const s = String(t || "").toLowerCase();
  if (s.startsWith("prim")) return "Primary";
  if (s.startsWith("sec")) return "Secondary";
  if (s.startsWith("tert")) return "Tertiary";
  return i === 0 ? "Primary" : i === 1 ? "Secondary" : "Other";
};
export const mapInsurance = (res) => {
  const list = Array.isArray(res)
    ? res // legacy bare array (fhir-mirror /insurance)
    : res && Array.isArray(res.coverages)
    ? res.coverages // new insurance-summary shape
    : res
    ? [res.primary, res.secondary].filter(Boolean)
    : [];
  return list
    .map((r, i) => ({
      type: INS_RANK(r.rank || r.type, i),
      plan: truncate(String(r.plan_name || r.insurancename || "").trim(), 40),
      policy: truncate(String(r.policy_number || r.subscriber_id || "").trim(), 24),
    }))
    .filter((r) => r.plan || r.policy)
    .slice(0, 2);
};

// rx pharmacy endpoint ({data:{clinicalprovidername, city, state, phonenumber,...}})
// -> {name, detail} for the default-pharmacy line on the form (Athena default pharmacy).
export const mapPharmacy = (res) => {
  const d = res && res.data && typeof res.data === "object" ? res.data : null;
  if (!d || d.error || !d.clinicalprovidername) return { name: "", detail: "" };
  const street = [d.address1, d.address2].filter(Boolean).join(" ").trim();
  const cityStateZip = [[d.city, d.state].filter(Boolean).join(", "), d.zip].filter(Boolean).join(" ").trim();
  const phone = fmtPhone(d.phonenumber);
  return {
    name: String(d.clinicalprovidername).trim(),
    detail: [street, cityStateZip, phone].filter(Boolean).join("  ·  "),
  };
};

/* ----------------------------- data loads ----------------------------- */

// Load allergies + reconciled-active meds for a patient (no caching here — the
// caller can memoize). DB-first (careiq nightly snapshot) -> on-demand ACTIVE
// refresh -> FHIR-mirror fallback.
export const loadEncounterClinical = async (patientid) => {
  if (!patientid)
    return { allergies: [], medications: [], surgeries: [], insurance: [], rpm: { enrolled: false, lastReading: "" }, pharmacy: { name: "", detail: "" }, partial: false };
  const result = { allergies: [], medications: [], surgeries: [], insurance: [], rpm: { enrolled: false, lastReading: "" }, pharmacy: { name: "", detail: "" }, partial: false };
  const [curMedsRes, allergyRes, surgRes, insRes, rpmRes, bppLatestRes, pharmRes] = await Promise.allSettled([
    api.get(`/v1/fhir-mirror/${practiceid}/current-medications/patient/${patientid}`),
    api.get(`/v1/fhir-mirror/${practiceid}/patients/${patientid}/allergies`),
    api.get(`/v1/fhir-mirror/${practiceid}/surgical-history-proprietary/patient/${patientid}`),
    api.get(`/v1/fhir/${practiceid}/patients/${patientid}/insurance-summary`),
    api.get(`/v1/${practiceid}/patient/${patientid}/rpm/data`),
    api.get(`/v1/${practiceid}/rpm/bpp-master/latest`),
    api.get(`/v1/${practiceid}/rx/patient/${patientid}/department/1/pharmacy`),
  ]);
  let meds = curMedsRes.status === "fulfilled" ? mapCurrentMeds(curMedsRes.value) : [];
  // No nightly snapshot yet (e.g. patient search / sign-in) — trigger a live
  // ACTIVE refresh (Athena medicationtype=ACTIVE), persist it, and re-read.
  if (!meds.length) {
    try {
      await api.post(`/v1/fhir-mirror/${practiceid}/current-medications/patient/${patientid}/refresh`, {});
      const reRes = await api.get(`/v1/fhir-mirror/${practiceid}/current-medications/patient/${patientid}`);
      meds = mapCurrentMeds(reRes);
    } catch (e) {
      /* fall through to FHIR */
    }
  }
  // Last resort: FHIR-mirror list (broader, no fill dates) if ACTIVE is empty.
  if (!meds.length) {
    try {
      const fhirRes = await api.post(`/v1/fhir-mirror/${practiceid}/medications/patient/${patientid}`, {});
      meds = mapMedications(fhirRes);
    } catch (e) {
      result.partial = true;
    }
  }
  result.medications = meds;
  if (allergyRes.status === "fulfilled") result.allergies = mapAllergies(allergyRes.value);
  else result.partial = true;
  if (surgRes.status === "fulfilled") result.surgeries = mapSurgeries(surgRes.value);
  if (insRes.status === "fulfilled") result.insurance = mapInsurance(insRes.value);
  if (rpmRes.status === "fulfilled") result.rpm = mapRpm(rpmRes.value);
  // Real last-reading date from bpp_master (via the latest-per-patient endpoint).
  if (bppLatestRes.status === "fulfilled") {
    const lr = mapLatestReading(bppLatestRes.value, patientid);
    if (lr) result.rpm.lastReading = lr;
  }
  if (pharmRes.status === "fulfilled") result.pharmacy = mapPharmacy(pharmRes.value);
  return result;
};

// Fetch demographics (FHIR) -> {name, dob, sex}. Tolerant of failure.
export const fetchPatientDemographics = async (patientid) => {
  try {
    const res = await api.get(`/v1/fhir-mirror/${practiceid}/patient/${patientid}`);
    const d = res && Array.isArray(res.data) ? res.data[0] : null;
    if (!d) return {};
    return {
      name: fmtName(d.lastname, d.firstname),
      dob: fmtDateSlash(d.dob),
      age: ageFromDob(d.dob),
      sex: fmtSex(d.gender),
    };
  } catch (e) {
    return {};
  }
};

// Fetch the patient's appointments for a given day from the schedule, EXCLUDING
// cancelled ones (visit_confirmation === "Cancelled"). Returns the correct appt
// time (earliest active appointment) + the distinct appointment types (purpose)
// for the day. Fixes the "time picked from a cancelled appointment" bug and
// feeds the appointment-types strip.
export const loadPatientDayAppointments = async (patientid, dateStr) => {
  const out = { time: "", types: [] };
  if (!patientid || !dateStr) return out;
  try {
    const res = await api.post(`/v1/${practiceid}/scheduler/schedule_list`, {
      startdate: dateStr,
      enddate: dateStr,
    });
    const arr = res && res.status === 200 && Array.isArray(res.data) ? res.data : [];
    const mine = arr.filter(
      (a) =>
        String(a.patientid) === String(patientid) &&
        String(a.visit_confirmation || "").trim().toLowerCase() !== "cancelled"
    );
    if (!mine.length) return out;
    mine.sort((a, b) => String(a.starttime || "").localeCompare(String(b.starttime || "")));
    out.time = fmtTime(mine[0].starttime);
    const seen = new Set();
    mine.forEach((a) => {
      const t = String(a.type || a.purpose || "").trim();
      if (t && !seen.has(t.toUpperCase())) {
        seen.add(t.toUpperCase());
        out.types.push(t.toUpperCase());
      }
    });
  } catch (e) {
    /* tolerant — appt strip/time just won't populate */
  }
  return out;
};

/* ----------------- one-shot print for a single patient ----------------- */

// Used by the Sign-in sheet's per-row Print button. Fetches demographics +
// clinical, builds the encounter form, and opens the browser print dialog.
// `name` / `provider` from the caller's row take precedence; dob/sex come from
// the demographics fetch. Returns true if some data was unavailable.
export const printEncounterForPatient = async ({ patientid, name, provider, dos, time, date } = {}) => {
  const apptDate = date || todayStr();
  const [demo, clinical, appts] = await Promise.all([
    fetchPatientDemographics(patientid),
    loadEncounterClinical(patientid),
    loadPatientDayAppointments(patientid, apptDate),
  ]);
  const data = {
    name: name || demo.name || "—",
    dob: demo.dob || "",
    age: demo.age || "",
    sex: demo.sex || "",
    mrn: String(patientid || ""),
    provider: provider || "",
    dos: dos || fmtDateSlash(apptDate),
    // active-appointment time (overrides any cancelled-appt time the caller passed)
    time: appts.time || time || "",
    apptTypes: appts.types,
    rpm: clinical.rpm || { enrolled: false, lastReading: "" },
    pharmacy: clinical.pharmacy || { name: "", detail: "" },
    insurance: clinical.insurance || [],
    surgeries: clinical.surgeries,
    allergies: clinical.allergies,
    medications: clinical.medications,
  };
  await printHtmlDocument(buildDocument([buildSheet(data)], `Encounter — ${data.name}`));
  return clinical.partial;
};
